import { createContext, useContext, ReactNode, useCallback, useMemo } from 'react'

/**
 * Generic interface for search params persistence context
 */
export interface SearchParamsContextValue {
  saveSearchParams: (params: any) => void
  restoreSearchParams: () => any
  clearSearchParams: () => void
  hasSearchParams: boolean
}

/**
 * Factory function to create a search params context with a specific storage key
 * @param storageKey - The sessionStorage key to use for persistence
 * @param contextName - Name for better error messages
 */
export function createSearchParamsContext(storageKey: string, contextName: string) {
  const Context = createContext<SearchParamsContextValue | undefined>(undefined)

  const useContextHook = () => {
    const context = useContext(Context)
    if (!context) {
      throw new Error(`use${contextName} must be used within ${contextName}Provider`)
    }
    return context
  }

  interface ProviderProps {
    children: ReactNode
  }

  const Provider: React.FC<ProviderProps> = ({ children }) => {
    const saveSearchParams = useCallback(
      (params: any) => {
        try {
          sessionStorage.setItem(storageKey, JSON.stringify(params))
        } catch (error) {
          console.error(`Error saving search params to ${storageKey}:`, error)
        }
      },
      [storageKey]
    )

    const restoreSearchParams = useCallback(() => {
      try {
        const saved = sessionStorage.getItem(storageKey)
        return saved ? JSON.parse(saved) : null
      } catch (error) {
        console.error(`Error restoring search params from ${storageKey}:`, error)
        return null
      }
    }, [storageKey])

    const clearSearchParams = useCallback(() => {
      try {
        sessionStorage.removeItem(storageKey)
      } catch (error) {
        console.error(`Error clearing search params from ${storageKey}:`, error)
      }
    }, [storageKey])

    const hasSearchParams = useMemo(() => {
      try {
        return !!sessionStorage.getItem(storageKey)
      } catch (error) {
        return false
      }
    }, [storageKey])

    const value = useMemo<SearchParamsContextValue>(
      () => ({
        saveSearchParams,
        restoreSearchParams,
        clearSearchParams,
        hasSearchParams,
      }),
      [saveSearchParams, restoreSearchParams, clearSearchParams, hasSearchParams]
    )

    return <Context.Provider value={value}>{children}</Context.Provider>
  }

  return {
    Context,
    Provider,
    useContext: useContextHook,
  }
}
